import { Zap, Clock, TrendingUp, Sparkles, Star } from "lucide-react";
import StandardSection from "@/components/ui/StandardSection";
import { useEffect, useState } from "react";
import logger from '@/utils/logger';
import { fetchContentJson, extractPanelData, getFieldValue, getNestedPanel } from "@/utils/contentJsonParser";

export default function PricingCTA() {

  const [loading, setLoading] = useState(false);
  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState<string>("");
  const [sectionSecondaryHeader, setSectionSecondaryHeader] = useState<string>("");
  const [sectionDescription, setSectionDescription] = useState<string>("");
  const [point1Title, setPoint1Title] = useState<string>("");
  const [point1Subtitle, setPoint1Subtitle] = useState<string>("");
  const [point2Title, setPoint2Title] = useState<string>("");
  const [point2Subtitle, setPoint2Subtitle] = useState<string>("");
  const [pricingValue, setPricingValue] = useState<string>("");
  const [pricingTitle, setPricingTitle] = useState<string>("");
  const [pricingPoints, setPricingPoints] = useState<string[]>([]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchContentJson();
        
        // Extract the revolutionary pricing model panel
        const pricingModelMainPanel = extractPanelData(data, "revolutionaryPricingModelPanel");
        
        if (pricingModelMainPanel) {
          // Extract main section fields
          setSectionTitle(getFieldValue(pricingModelMainPanel, "pricing_title", ""));
          setSectionPrimaryHeader(getFieldValue(pricingModelMainPanel, "pricing_primary_header", ""));
          setSectionSecondaryHeader(getFieldValue(pricingModelMainPanel, "pricing_secondary_header", ""));
          setSectionDescription(getFieldValue(pricingModelMainPanel, "pricing_description", ""));

          // Extract additional points from nested panels
          const point1Panel = getNestedPanel(pricingModelMainPanel, "additionalPoint1Panel");
          if (point1Panel) {
            setPoint1Title(getFieldValue(point1Panel, "point1_title", ""));
            setPoint1Subtitle(getFieldValue(point1Panel, "point1_subtitle", ""));
          }

          const point2Panel = getNestedPanel(pricingModelMainPanel, "additionalPoint2Panel");
          if (point2Panel) {
            setPoint2Title(getFieldValue(point2Panel, "point2_title", ""));
            setPoint2Subtitle(getFieldValue(point2Panel, "point2_subtitle", ""));
          }

          // Extract pricing model panel (package offer)
          const pricingModelPanel = getNestedPanel(pricingModelMainPanel, "pricingModelPanel");
          if (pricingModelPanel) {
            setPricingValue(getFieldValue(pricingModelPanel, "pricing_value", ""));
            setPricingTitle(getFieldValue(pricingModelPanel, "pricing_point_title", ""));
            
            const pricingPointsValue = getFieldValue(pricingModelPanel, "pricing_points", []);
            if (Array.isArray(pricingPointsValue)) {
              setPricingPoints(pricingPointsValue.map((pt: any) => pt.point));
            } else {
              setPricingPoints([]);
            }
          }
        }
        
        setLoading(false);
      } catch (error) {
        logger.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <StandardSection id="pricing" background="gradient">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
        {/* Left Content */}
        <div className="space-y-6 sm:space-y-8 lg:pr-8 px-4 sm:px-0">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-100 to-forest-100 border border-primary-200 text-primary-700 px-6 py-3 rounded-full text-sm shadow-sm">
            <Sparkles className="h-4 w-4 text-primary-600" />
            <span className="text-lg sm:text-xl leading-relaxed">{sectionTitle}</span>
            <Zap className="h-4 w-4 text-forest-custom" />
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles">
            <span className="text-gray-900 block break-words whitespace-normal lg:whitespace-nowrap">
              {sectionPrimaryHeader}
            </span>
            <span className="bg-gradient-to-r text-green-600 bg-clip-text block break-words whitespace-normal lg:whitespace-nowrap">
              {sectionSecondaryHeader}
            </span>
          </h2>

          <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
            {sectionDescription}
          </p>

          {/* Feature highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {(point1Title || point1Subtitle) && (
              <div className="flex items-center gap-2 sm:gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-primary-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
                <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 float-animation flex-shrink-0" />
                <div>
                  <div className="text-gray-900 font-semibold text-sm sm:text-base">{point1Title}</div>
                  <div className="text-gray-600 text-xs sm:text-sm">{point1Subtitle}</div>
                </div>
              </div>
            )}
            {(point2Title || point2Subtitle) && (
              <div className="flex items-center gap-2 sm:gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-forest-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
                <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-forest-custom float-animation flex-shrink-0" />
                <div>
                  <div className="text-gray-900 font-semibold text-sm sm:text-base">{point2Title}</div>
                  <div className="text-gray-600 text-xs sm:text-sm">{point2Subtitle}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Content - Futuristic Price Card */}
        <div className="flex justify-center lg:justify-end px-4 sm:px-0">
          <div className="relative group max-w-sm sm:max-w-md w-full">
            {/* Modern background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600/10 via-transparent to-forest-custom/10 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>

            {/* Main price card */}
            <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-10 border border-primary-200 shadow-2xl hover:transform hover:scale-105 transition-all duration-500 text-center">
              {/* Top accent */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-1 bg-green-600 rounded-full"></div>

              {/* Stars decoration */}
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-accent-amber fill-current mx-1" />
                ))}
              </div>

              <div className="text-green-600 sm:text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r mb-3">
                {pricingValue && !isNaN(Number(pricingValue))
                  ? Number(pricingValue)?.toLocaleString()
                  : pricingValue}
              </div>
              <div className="text-gray-600 text-base sm:text-lg lg:text-xl font-medium mb-4 sm:mb-6">{pricingTitle}</div>

              {/* Value propositions */}
              <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {pricingPoints.map((pt, idx) => (
                  <div key={pt + idx} className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-600">
                    <div className={`w-2 h-2 rounded-full animate-pulse flex-shrink-0 ${idx % 2 === 0 ? "bg-primary-600" : "bg-forest-custom"}`}></div>
                    {pt}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </StandardSection>
  );
}
